import React from 'react';
import {
  TextField,
  TextareaField,
  SelectField,
  ToggleSwitchField,
  Card,
  Button
} from '@dopomogai/ui';
import { MessageCircle, Settings, Brain, Plus, Trash2 } from 'lucide-react';
import type { WidgetConfig, RuleBasedMessage } from '../types/widget';
import { welcomeStrategyOptions, mobileModeOptions } from '../utils/widgetDefaults';

interface BehaviorTabContentProps {
  config: WidgetConfig;
  onConfigChange: (field: string, value: any) => void;
  availableModels: Array<{ id: string; name: string }>;
  isFieldVisible?: (path: string) => boolean;
}

export const BehaviorTabContent: React.FC<BehaviorTabContentProps> = ({
  config,
  onConfigChange,
  availableModels,
  isFieldVisible,
}) => {
  const canShow = isFieldVisible ?? (() => true);

  const showWelcome = ['welcomeStrategy', 'defaultWelcome', 'ruleBasedWelcome', 'llmWelcomePrompt', 'llmWelcomeModel'].some(canShow);
  const showRuntimeMode = [
    'preset',
    'features.attachmentsEnabled',
    'features.historyEnabled',
    'features.tabsEnabled',
    'features.threadsEnabled',
    'features.commentsEnabled',
  ].some(canShow);
  const showChatBehavior = [
    'showThinkingProcess',
    'streamingEnabled',
    'appearance.showCollapsedThinking',
    'appearance.maxMessageLength',
  ].some(canShow);
  const showOpenBehavior = [
    'defaultMinimizedDesktop',
    'defaultMinimizedMobile',
    'autoOpen.delayDesktop',
    'autoOpen.delayMobile',
    'autoOpen.oncePerSession',
  ].some(canShow);
  const showContent = [
    'markdownSupport.enableUserMarkdown',
    'markdownSupport.enableAssistantMarkdown',
    'contextPrepending.url',
    'contextPrepending.hostname',
    'contextPrepending.store',
  ].some(canShow);
  const showMobile = ['mobileMode', 'mobileHalfHeight'].some(canShow);
  const handleRuleChange = (index: number, field: keyof RuleBasedMessage, value: string) => {
    const newRules = [...config.ruleBasedWelcome];
    newRules[index] = { ...newRules[index], [field]: value };
    onConfigChange('ruleBasedWelcome', newRules);
  };

  const addRule = () => {
    const newRules = [...config.ruleBasedWelcome, { if_url_contains: '', if_hostname_is: '', message: '' }];
    onConfigChange('ruleBasedWelcome', newRules);
  };

  const removeRule = (index: number) => {
    const newRules = config.ruleBasedWelcome.filter((_, i) => i !== index);
    onConfigChange('ruleBasedWelcome', newRules);
  };

  const modelOptions = availableModels.map(model => ({
    value: model.id,
    label: model.name
  }));

  const presetOptions = [
    { value: 'studio', label: 'Studio (advanced)' },
    { value: 'simple', label: 'Simple (single-thread)' },
  ];

  const handlePresetChange = (value: string) => {
    onConfigChange('preset', value);

    if (value === 'simple') {
      onConfigChange('features.threadsEnabled', false);
      onConfigChange('features.commentsEnabled', false);
      onConfigChange('features.historyEnabled', false);
      onConfigChange('features.tabsEnabled', false);
      onConfigChange('features.attachmentsEnabled', false);
      return;
    }

    if (value === 'studio') {
      onConfigChange('features.historyEnabled', true);
      onConfigChange('features.tabsEnabled', true);
      onConfigChange('features.attachmentsEnabled', true);
    }
  };

  const autoOpen = config.autoOpen || { delayDesktop: 0, delayMobile: 0, oncePerSession: false };

  return (
    <div className="space-y-6">
      {showRuntimeMode && (
        <Card
          title="Runtime Mode"
          icon={<Settings className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-violet-100"
        >
          <div className="space-y-4">
            {canShow('preset') && (
              <SelectField
                label="Preset"
                name="preset"
                value={config.preset || 'studio'}
                options={presetOptions}
                onChange={(value) => handlePresetChange(String(value))}
              />
            )}

            {canShow('features.attachmentsEnabled') && (
              <ToggleSwitchField
                label="Enable Attachments"
                checked={!!config.features.attachmentsEnabled}
                onChange={(e) => onConfigChange('features.attachmentsEnabled', e.target.checked)}
              />
            )}

            {canShow('features.historyEnabled') && (
              <ToggleSwitchField
                label="Enable Conversation History"
                checked={!!config.features.historyEnabled}
                onChange={(e) => onConfigChange('features.historyEnabled', e.target.checked)}
              />
            )}

            {canShow('features.tabsEnabled') && (
              <ToggleSwitchField
                label="Enable Tabs"
                checked={!!config.features.tabsEnabled}
                onChange={(e) => onConfigChange('features.tabsEnabled', e.target.checked)}
              />
            )}

            {canShow('features.threadsEnabled') && (
              <ToggleSwitchField
                label="Enable Threads"
                checked={!!config.features.threadsEnabled}
                onChange={(e) => onConfigChange('features.threadsEnabled', e.target.checked)}
              />
            )}

            {canShow('features.commentsEnabled') && (
              <ToggleSwitchField
                label="Enable Comments"
                checked={!!config.features.commentsEnabled}
                onChange={(e) => onConfigChange('features.commentsEnabled', e.target.checked)}
              />
            )}
          </div>
        </Card>
      )}

      {showWelcome && (
        <Card
          title="Welcome Strategy"
          icon={<MessageCircle className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-sky-100"
        >
          <div className="space-y-4">
            {canShow('welcomeStrategy') && (
              <SelectField
                label="Strategy Type"
                name="welcomeStrategy"
                value={config.welcomeStrategy}
                options={welcomeStrategyOptions}
                onChange={(value) => onConfigChange('welcomeStrategy', value)}
              />
            )}

            {config.welcomeStrategy === 'default' && canShow('defaultWelcome') && (
              <TextareaField
                label="Default Welcome Message"
                value={config.defaultWelcome}
                onChange={(e) => onConfigChange('defaultWelcome', e.target.value)}
                rows={3}
                placeholder="Hello there! I'm {botName}. How can I assist you today?"
              />
            )}

            {config.welcomeStrategy === 'rules' && canShow('ruleBasedWelcome') && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Rule-Based Messages</label>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Plus className="w-4 h-4" />}
                    text="Add Rule"
                    onClick={addRule}
                  />
                </div>

                {config.ruleBasedWelcome.map((rule, index) => (
                  <Card key={index} noPadding={false} className="bg-slate-50">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-600">Rule {index + 1}</span>
                        {config.ruleBasedWelcome.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<Trash2 className="w-4 h-4" />}
                            onClick={() => removeRule(index)}
                          />
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <TextField
                          label="URL Contains"
                          value={rule.if_url_contains}
                          onChange={(e) => handleRuleChange(index, 'if_url_contains', e.target.value)}
                          placeholder="/pricing"
                        />
                        <TextField
                          label="Hostname Is"
                          value={rule.if_hostname_is}
                          onChange={(e) => handleRuleChange(index, 'if_hostname_is', e.target.value)}
                          placeholder="example.com (optional)"
                        />
                      </div>

                      <TextareaField
                        label="Message"
                        value={rule.message}
                        onChange={(e) => handleRuleChange(index, 'message', e.target.value)}
                        rows={2}
                        placeholder="Welcome message for this rule..."
                      />
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {config.welcomeStrategy === 'llm' && (
              <div className="space-y-4">
                {canShow('llmWelcomeModel') && (
                  <SelectField
                    label="LLM Model"
                    name="llmWelcomeModel"
                    value={config.llmWelcomeModel}
                    options={modelOptions}
                    onChange={(value) => onConfigChange('llmWelcomeModel', value)}
                    placeholder="Select a model..."
                  />
                )}

                {canShow('llmWelcomePrompt') && (
                  <TextareaField
                    label="LLM Welcome Prompt"
                    value={config.llmWelcomePrompt}
                    onChange={(e) => onConfigChange('llmWelcomePrompt', e.target.value)}
                    rows={4}
                    placeholder="You are {botName}, a friendly AI assistant. A user has just landed on the page: {page_url}. Craft a short, engaging welcome message..."
                  />
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {showChatBehavior && (
        <Card
          title="Chat Behavior"
          icon={<Settings className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-emerald-100"
        >
          <div className="space-y-4">
            {canShow('showThinkingProcess') && (
              <ToggleSwitchField
                label="Show Thinking Process"
                checked={config.showThinkingProcess}
                onChange={(e) => onConfigChange('showThinkingProcess', e.target.checked)}
              />
            )}

            {canShow('streamingEnabled') && (
              <ToggleSwitchField
                label="Enable Streaming"
                checked={config.streamingEnabled}
                onChange={(e) => onConfigChange('streamingEnabled', e.target.checked)}
              />
            )}

            {canShow('appearance.showCollapsedThinking') && (
              <ToggleSwitchField
                label="Show Collapsed Thinking"
                checked={config.appearance.showCollapsedThinking}
                onChange={(e) => onConfigChange('appearance.showCollapsedThinking', e.target.checked)}
              />
            )}

            {canShow('appearance.maxMessageLength') && (
              <TextField
                label="Max Message Length"
                value={String(config.appearance.maxMessageLength)}
                onChange={(e) => onConfigChange('appearance.maxMessageLength', parseInt(e.target.value, 10) || 0)}
                placeholder="2500"
              />
            )}
          </div>
        </Card>
      )}

      {showOpenBehavior && (
        <Card
          title="Open Behavior"
          icon={<Settings className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-cyan-100"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {canShow('defaultMinimizedDesktop') && (
                <ToggleSwitchField
                  label="Default Minimized (Desktop)"
                  checked={!!config.defaultMinimizedDesktop}
                  onChange={(e) => onConfigChange('defaultMinimizedDesktop', e.target.checked)}
                />
              )}
              {canShow('defaultMinimizedMobile') && (
                <ToggleSwitchField
                  label="Default Minimized (Mobile)"
                  checked={!!config.defaultMinimizedMobile}
                  onChange={(e) => onConfigChange('defaultMinimizedMobile', e.target.checked)}
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {canShow('autoOpen.delayDesktop') && (
                <TextField
                  label="Auto-open Delay (Desktop, ms)"
                  value={String(autoOpen.delayDesktop ?? 0)}
                  onChange={(e) => onConfigChange('autoOpen.delayDesktop', parseInt(e.target.value, 10) || 0)}
                  placeholder="0"
                />
              )}
              {canShow('autoOpen.delayMobile') && (
                <TextField
                  label="Auto-open Delay (Mobile, ms)"
                  value={String(autoOpen.delayMobile ?? 0)}
                  onChange={(e) => onConfigChange('autoOpen.delayMobile', parseInt(e.target.value, 10) || 0)}
                  placeholder="0"
                />
              )}
              {canShow('autoOpen.oncePerSession') && (
                <ToggleSwitchField
                  label="Open Once Per Session"
                  checked={!!autoOpen.oncePerSession}
                  onChange={(e) => onConfigChange('autoOpen.oncePerSession', e.target.checked)}
                />
              )}
            </div>
          </div>
        </Card>
      )}

      {showContent && (
        <Card
          title="Content & Context"
          icon={<Brain className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-yellow-100"
        >
          <div className="space-y-4">
            {canShow('markdownSupport.enableUserMarkdown') && (
              <ToggleSwitchField
                label="Enable User Markdown"
                checked={config.markdownSupport.enableUserMarkdown}
                onChange={(e) => onConfigChange('markdownSupport.enableUserMarkdown', e.target.checked)}
              />
            )}

            {canShow('markdownSupport.enableAssistantMarkdown') && (
              <ToggleSwitchField
                label="Enable Assistant Markdown"
                checked={config.markdownSupport.enableAssistantMarkdown}
                onChange={(e) => onConfigChange('markdownSupport.enableAssistantMarkdown', e.target.checked)}
              />
            )}

            {canShow('contextPrepending.url') && (
              <ToggleSwitchField
                label="Include Page URL in Context"
                checked={config.contextPrepending.url}
                onChange={(e) => onConfigChange('contextPrepending.url', e.target.checked)}
              />
            )}

            {canShow('contextPrepending.hostname') && (
              <ToggleSwitchField
                label="Include Hostname in Context"
                checked={config.contextPrepending.hostname}
                onChange={(e) => onConfigChange('contextPrepending.hostname', e.target.checked)}
              />
            )}

            {canShow('contextPrepending.store') && (
              <ToggleSwitchField
                label="Include Store/Cart Summary"
                checked={config.contextPrepending.store}
                onChange={(e) => onConfigChange('contextPrepending.store', e.target.checked)}
              />
            )}
          </div>
        </Card>
      )}

      {showMobile && (
        <Card
          title="Mobile Behavior"
          icon={<Settings className="w-5 h-5" />}
          noPadding={false}
          headerBgColor="bg-purple-100"
        >
          <div className="space-y-4">
            {canShow('mobileMode') && (
              <SelectField
                label="Mobile Mode"
                name="mobileMode"
                value={config.mobileMode}
                options={mobileModeOptions}
                onChange={(value) => onConfigChange('mobileMode', value)}
              />
            )}
            {config.mobileMode === 'half' && canShow('mobileHalfHeight') && (
              <TextField
                label="Half-sheet Height"
                value={config.mobileHalfHeight}
                onChange={(e) => onConfigChange('mobileHalfHeight', e.target.value)}
                placeholder="50dvh"
              />
            )}
          </div>
        </Card>
      )}
    </div>
  );
};
